import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import { Menu, X } from "lucide-react";
import analogLogoWordmark from "@/assets/analog-logo-wordmark.webp";
import { COLORS, typography } from "@/styles/may-theme";
import PostEventNotice from "./PostEventNotice";


interface MayHeaderProps {
  /** If true, header starts transparent and becomes solid on scroll */
  transparentOnTop?: boolean;
  /** Force light or dark text regardless of scroll state */
  forceLightText?: boolean;
  /** If true, shows only the logo (no nav, no CTA, no mobile menu) */
  minimal?: boolean;
  /**
   * Suppresses the fixed post-event notice bar. Only for pages that state the
   * post-event status prominently in their own content (the closeout homepage),
   * so the notice is never repeated twice at the top of the same screen.
   */
  hideNotice?: boolean;
}

const MayHeader = ({ transparentOnTop = false, forceLightText = false, minimal = false, hideNotice = false }: MayHeaderProps) => {
  const [hasScrolled, setHasScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { scrollY } = useScroll();
  
  useMotionValueEvent(scrollY, "change", (latest) => {
    setHasScrolled(latest > 100);
  });

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Post-event: editorial navigation only. No ticket, lodging, giveaway or
  // booking entry points may appear in public navigation.
  const navItems = [
    { label: 'Story', path: '/story' },
    { label: 'Lineup', path: '/lineup' },
    { label: 'Schedule', path: '/schedule' },
    { label: 'Experience', path: '/experience' },
    { label: 'FAQ', path: '/faq' },
    { label: 'Contact', path: '/contact' },
  ];

  
  // Determine text color based on scroll state and props
  const getTextColor = () => {
    if (forceLightText) return COLORS.dustySky;
    if (!transparentOnTop) return COLORS.charcoal;
    return hasScrolled ? COLORS.charcoal : COLORS.dustySky;
  };

  const getBgColor = () => {
    if (!transparentOnTop) return COLORS.dustySky;
    return hasScrolled ? COLORS.dustySky : 'transparent';
  };

  const textColor = getTextColor();
  const bgColor = getBgColor();

  return (
    <>
      {!hideNotice && <PostEventNotice />}
      <motion.header 
        className={`fixed ${hideNotice ? "top-0" : "top-16 md:top-11"} left-0 right-0 z-50 flex items-center justify-between px-6 md:px-10 py-4`}
        style={{ backgroundColor: bgColor }}
      >

        {/* Header logo */}
        <Link to="/" className="relative z-10">
          <img 
            src={analogLogoWordmark} 
            alt="Analog" 
            className="h-7 md:h-9 w-auto transition-all duration-300"
            style={{ 
              filter: textColor === COLORS.dustySky 
                ? 'brightness(0) invert(1)' 
                : 'brightness(0) saturate(0)',
            }}
          />
        </Link>

        {/* Desktop Navigation - hidden in minimal mode */}
        {!minimal && (
          <nav className="hidden md:flex items-center gap-10">
            {navItems.map((item) => (
              <Link 
                key={item.label}
                to={item.path}
                className="transition-opacity hover:opacity-70"
                style={{ 
                  ...typography.caption,
                  color: textColor,
                  letterSpacing: '0.1em',
                  fontSize: '12px',
                }}
              >
                {item.label.toUpperCase()}
              </Link>
            ))}
          </nav>
        )}

        {/* Post-event: no ticket CTA. Spacer keeps the nav centered. */}
        {!minimal && <div className="hidden md:block w-[120px]" aria-hidden />}


        {/* Mobile Menu Button - hidden in minimal mode */}
        {!minimal && (
          <button
            className="md:hidden relative z-10 p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {mobileMenuOpen ? (
              <X size={24} style={{ color: COLORS.charcoal }} />
            ) : (
              <Menu size={24} style={{ color: textColor }} />
            )}
          </button>
        )}
      </motion.header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <motion.div
          className="fixed inset-0 z-40 flex flex-col"
          style={{ backgroundColor: COLORS.dustySky }}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex-1 flex flex-col items-center px-6 pt-24 pb-10">
            <div className="flex flex-col items-center justify-center gap-8 pt-10">
            {navItems.map((item) => (
              <Link 
                key={item.label}
                to={item.path}
                className="transition-opacity hover:opacity-70"
                style={{ 
                  ...typography.headline,
                  color: COLORS.charcoal,
                  fontSize: '2rem',
                  textTransform: 'uppercase',
                }}
              >
                {item.label}
              </Link>
            ))}
            </div>
          </div>

        </motion.div>
      )}
    </>
  );
};

export default MayHeader;
